import type { LocationOnboarding } from '../api/config';

// Shared onboarding-status presentation, used by both the location detail page
// (header chip) and the locations list (column). Single source of truth for the
// labels/tones so the two views never drift.
//
// `tone` is a semantic token the call sites map to their own chip system
// (the detail page uses CSS chip-* classes; the list uses MUI <Chip color>),
// since those two render in different styling worlds.

export type OnboardingState = LocationOnboarding['state']; // 'not_started' | 'in_progress' | 'complete' | null

export type OnboardingTone = 'success' | 'warning' | 'neutral' | 'na';

export interface OnboardingDisplay {
  label: string;
  tone: OnboardingTone;
  icon: string; // material icon name
  description: string; // tooltip / a11y text
}

// `null`/`undefined` → N/A: the location has no registry row, i.e. it was never
// run through the Edge Onboard app (true for most existing locations, which
// predate it). This is intentionally distinct from `not_started` (the app knows
// the location but onboarding hasn't begun) so we don't falsely imply the app
// tracked a legacy location.
export function onboardingDisplay(state: OnboardingState | undefined): OnboardingDisplay {
  switch (state) {
    case 'complete':
      return { label: 'Onboarded', tone: 'success', icon: 'check_circle', description: 'Onboarding completed in the Edge Onboard app.' };
    case 'in_progress':
      return { label: 'Onboarding', tone: 'warning', icon: 'pending', description: 'Onboarding in progress in the Edge Onboard app.' };
    case 'not_started':
      return { label: 'Not Onboarded', tone: 'neutral', icon: 'radio_button_unchecked', description: 'Tracked by the Edge Onboard app; onboarding not started yet.' };
    default:
      return { label: 'N/A', tone: 'na', icon: 'remove', description: 'Not tracked by the Edge Onboard app (location predates it).' };
  }
}

// MUI <Chip> color for the list view.
export function onboardingMuiColor(tone: OnboardingTone): 'success' | 'warning' | 'default' {
  switch (tone) {
    case 'success': return 'success';
    case 'warning': return 'warning';
    default: return 'default'; // neutral + na both render muted
  }
}

// Detail-page CSS chip class (defined in LocationDashboardPage.css).
export function onboardingChipClass(tone: OnboardingTone): string {
  switch (tone) {
    case 'success': return 'chip-success';
    case 'warning': return 'chip-warning';
    default: return 'chip-default'; // neutral + na
  }
}
